/**
 * Fetches gallery videos (same as Profile gallery videos tab) and returns
 * slots up to the plan limit, with gray placeholders for missing slots.
 */

import { useEffect, useState } from "react";
import { ArtistService } from "@/services/artistService";
import type { GalleryVideoItem } from "@/services/artistService";

export const VIDEO_LIMITS = {
  free: 1,
  economy: 2,
  premium: 4,
  elite: 6,
} as const;

export type VideoPlan = keyof typeof VIDEO_LIMITS;

export type VideoSourceForPlayer = {
  url: string;
  type: "youtube" | "vimeo" | "facebook" | "instagram" | "mp4";
  aspect?: "horizontal" | "vertical";
};

function getVideoTypeFromUrl(url: string): VideoSourceForPlayer["type"] {
  if (!url || typeof url !== "string") return "mp4";
  const u = url.trim().toLowerCase();
  if (/youtube\.com|youtu\.be/.test(u)) return "youtube";
  if (/vimeo\.com/.test(u)) return "vimeo";
  if (/facebook\.com|fb\.watch|fb\.com/.test(u)) return "facebook";
  if (/instagram\.com/.test(u)) return "instagram";
  return "mp4";
}

export type VideoSlot =
  | { type: "video"; id: number; url: string; source: VideoSourceForPlayer }
  | { type: "placeholder"; index: number };

export function useGalleryVideosForPreview(plan: VideoPlan) {
  const [videos, setVideos] = useState<GalleryVideoItem[]>([]);
  const [loading, setLoading] = useState(true);

  const limit = VIDEO_LIMITS[plan];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ArtistService.getGalleryVideos()
      .then((data) => {
        if (!cancelled) setVideos(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setVideos([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const slots: VideoSlot[] = [];
  for (let i = 0; i < limit; i++) {
    const video = videos[i];
    if (video?.url) {
      slots.push({
        type: "video",
        id: video.id,
        url: video.url,
        source: {
          url: video.url,
          type: getVideoTypeFromUrl(video.url),
          aspect: "horizontal",
        },
      });
    } else {
      slots.push({ type: "placeholder", index: i });
    }
  }

  return {
    slots,
    videos,
    loading,
    limit,
  };
}
